import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import { PageTransitionWrapper } from "./PageTransitionWrapper";
import { ScrollToTop } from "./ScrollToTop";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ProtectedMemberRoute, ProtectedAdminMemberRoute } from "@/components/ProtectedMemberRoute";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";

// Critical routes - keep as static imports for instant load
import Index from "@/pages/Index";

// NotFound rarely needed - lazy load to reduce initial JS
const NotFound = lazy(() => import("@/pages/NotFound"));

// SEO-critical pages use lazy loading with Suspense fallback;
// Googlebot renders JS-based SPAs correctly since 2019
const InspecoesPeriodicasPublicPage = lazy(() => import("@/pages/seo/InspecoesPeriodicasPublicPage"));

// Lazy load all other pages to reduce initial bundle size and improve FID
const ClerkAuthPage = lazy(() => import("@/pages/ClerkAuthPage"));
const CreateOrganizationPage = lazy(() => import("@/pages/CreateOrganizationPage"));
const ComoFuncionaPage = lazy(() => import("@/pages/ComoFuncionaPage"));
const PlanosConsultoriaPage = lazy(() => import("@/pages/PlanosConsultoriaPage"));
const ClientDashboard = lazy(() => import("@/pages/ClientDashboard"));
const AuditRequestPage = lazy(() => import("@/pages/AuditRequestPage"));

const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminAuditDetail = lazy(() => import("@/pages/AdminAuditDetail"));
const AdminAuditRequestsPage = lazy(() => import("@/pages/AdminAuditRequestsPage"));
const AdminAuditRequestDetailPage = lazy(() => import("@/pages/AdminAuditRequestDetailPage"));
const SetupAdminPage = lazy(() => import("@/pages/SetupAdminPage"));
const ChangelogPage = lazy(() => import("@/pages/ChangelogPage"));
const GovernanceDashboard = lazy(() => import("@/pages/GovernanceDashboard"));
const BoardDashboard = lazy(() => import("@/pages/BoardDashboard"));
const InvestorsPage = lazy(() => import("@/pages/InvestorsPage"));
const PartnersPage = lazy(() => import("@/pages/PartnersPage"));
const EmailTemplatesPage = lazy(() => import("@/pages/EmailTemplatesPage"));
const CallScriptPage = lazy(() => import("@/pages/CallScriptPage"));
const InvestorDeckPage = lazy(() => import("@/pages/InvestorDeckPage"));
const InvestorDeckVisualPage = lazy(() => import("@/pages/InvestorDeckVisualPage"));
const MeetingPlaybookPage = lazy(() => import("@/pages/MeetingPlaybookPage"));

const FAQDueDiligencePage = lazy(() => import("@/pages/FAQDueDiligencePage"));
const QAFinanceiroPage = lazy(() => import("@/pages/QAFinanceiroPage"));
const LearningLoopPage = lazy(() => import("@/pages/LearningLoopPage"));
const DataPolicyPage = lazy(() => import("@/pages/DataPolicyPage"));
const AuditResponsePage = lazy(() => import("@/pages/AuditResponsePage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));

const AuditoriaContratoManutencaoPage = lazy(() => import("@/pages/AuditoriaContratoManutencaoPage"));
const AnaliseContratoElevadorPage = lazy(() => import("@/pages/tools/AnaliseContratoElevadorPage"));
const SimuladorRiscoElevadorPage = lazy(() => import("@/pages/tools/SimuladorRiscoElevadorPage"));
const SegundaOpiniaoTecnicaPage = lazy(() => import("@/pages/SegundaOpiniaoTecnicaPage"));


const CustoAuditoriaTecnicaPage = lazy(() => import("@/pages/CustoAuditoriaTecnicaPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));

const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const MembersTermsPage = lazy(() => import("@/pages/legal/MembersTermsPage"));
const RefundPolicyPage = lazy(() => import("@/pages/legal/RefundPolicyPage"));
const NotaConfiancaPage = lazy(() => import("@/pages/legal/NotaConfiancaPage"));
const LivroReclamacoesPage = lazy(() => import("@/pages/legal/LivroReclamacoesPage"));
const ContactosPage = lazy(() => import("@/pages/legal/ContactosPage"));
const DiagnosticoElevadorPage = lazy(() => import("@/pages/DiagnosticoElevadorPage"));
const PoliticaImagensIAPage = lazy(() => import("@/pages/legal/PoliticaImagensIAPage"));
const SobrePage = lazy(() => import("@/pages/SobrePage"));
const GovernancaDadosPage = lazy(() => import("@/pages/GovernancaDadosPage"));
const EngenheiroResponsavelPage = lazy(() => import("@/pages/EngenheiroResponsavelPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const MembersPage = lazy(() => import("@/pages/MembersPage"));

const OnboardingPage = lazy(() => import("@/pages/OnboardingPage"));

const MembersSuccessPage = lazy(() => import("@/pages/MembersSuccessPage"));

const MembersOnboardingPage = lazy(() => import("@/pages/MembersOnboardingPage"));
const MembersAuthPage = lazy(() => import("@/pages/MembersAuthPage"));
const MembersDashboardPage = lazy(() => import("@/pages/MembersDashboardPage"));
const MembersNewsPage = lazy(() => import("@/pages/MembersNewsPage"));
const MembersSupportPage = lazy(() => import("@/pages/MembersSupportPage"));
const MembersChatPage = lazy(() => import("@/pages/MembersChatPage"));
const MembersAccountPage2 = lazy(() => import("@/pages/MembersAccountPage2"));
const MembersAdminPage = lazy(() => import("@/pages/MembersAdminPage"));
const AdminMembersPage = lazy(() => import("@/pages/AdminMembersPage"));
const AdminManagementPage = lazy(() => import("@/pages/AdminManagementPage"));
const AdminAuditLogsPage = lazy(() => import("@/pages/AdminAuditLogsPage"));
const AdminNewsPage = lazy(() => import("@/pages/AdminNewsPage"));
const AdminTemplatesPage = lazy(() => import("@/pages/AdminTemplatesPage"));
const AdminDocumentsPage = lazy(() => import("@/pages/AdminDocumentsPage"));
const AdminLeadsPage = lazy(() => import("@/pages/AdminLeadsPage"));
const AdminGlossaryPage = lazy(() => import("@/pages/admin/AdminGlossaryPage"));
const AdminSeoReportPage = lazy(() => import("@/pages/admin/AdminSeoReportPage"));
const AdminInventarioPage = lazy(() => import("@/pages/admin/AdminInventarioPage"));
const AdminMonitorNormasPage = lazy(() => import("@/pages/admin/AdminMonitorNormasPage"));
const AdminSeoWarRoomPage = lazy(() => import("@/pages/admin/AdminSeoWarRoomPage"));
const GlossaryPage = lazy(() => import("@/pages/members/GlossaryPage"));
const GlossaryTermPage = lazy(() => import("@/pages/members/GlossaryTermPage"));
const UpgradePage = lazy(() => import("@/pages/members/UpgradePage"));
const ChecklistsPage = lazy(() => import("@/pages/members/ChecklistsPage"));
const ComparisonsPage = lazy(() => import("@/pages/members/ComparisonsPage"));
const ComparisonDetailPage = lazy(() => import("@/pages/members/ComparisonDetailPage"));
const InspectionsPage = lazy(() => import("@/pages/members/InspectionsPage"));
const VIPDashboardPage = lazy(() => import("@/pages/members/VIPDashboardPage"));
const VIPCasesPage = lazy(() => import("@/pages/members/VIPCasesPage"));
const VIPCaseDetailPage = lazy(() => import("@/pages/members/VIPCaseDetailPage"));
const VIPOpinionsPage = lazy(() => import("@/pages/members/VIPOpinionsPage"));
const VIPOpinionDetailPage = lazy(() => import("@/pages/members/VIPOpinionDetailPage"));
const VIPRiskAnalysesPage = lazy(() => import("@/pages/members/VIPRiskAnalysesPage"));
const VIPRiskAnalysisDetailPage = lazy(() => import("@/pages/members/VIPRiskAnalysisDetailPage"));
const VIPTemplatesPage = lazy(() => import("@/pages/members/VIPTemplatesPage"));

const ElevadoresPage = lazy(() => import("@/pages/seo/ElevadoresPage"));
const CertificacaoElevadoresPortugalPage = lazy(() => import("@/pages/seo/CertificacaoElevadoresPortugalPage"));
const ModernizacaoElevadoresPage = lazy(() => import("@/pages/seo/ModernizacaoElevadoresPage"));
const AuditoriaElevadoresPage = lazy(() => import("@/pages/seo/AuditoriaElevadoresPage"));
const ConsultoriaElevadoresPage = lazy(() => import("@/pages/seo/ConsultoriaElevadoresPage"));
const ConsultorElevadoresCondominioPage = lazy(() => import("@/pages/seo/ConsultorElevadoresCondominioPage"));
const AuditoriaElevadoresCondominioPage = lazy(() => import("@/pages/seo/AuditoriaElevadoresCondominioPage"));





const ChecklistElevadoresPage = lazy(() => import("@/pages/seo/ChecklistElevadoresPage"));

const ConditionalEN8120Page = lazy(() => import("@/pages/seo/ConditionalEN8120Page"));
const ConditionalEN8150Page = lazy(() => import("@/pages/seo/ConditionalEN8150Page"));
const ConditionalEN81AcessibilidadePage = lazy(() => import("@/pages/seo/ConditionalEN81AcessibilidadePage"));
const ConditionalEN81IncendioPage = lazy(() => import("@/pages/seo/ConditionalEN81IncendioPage"));

const SegurancaElevadoresPage = lazy(() => import("@/pages/seo/SegurancaElevadoresPage"));

const FalhasComunsSistemasSegurancaPage = lazy(() => import("@/pages/seo/FalhasComunsSistemasSegurancaPage"));
const ChecklistInspecaoSegurancaPage = lazy(() => import("@/pages/seo/ChecklistInspecaoSegurancaPage"));
const EN81MecanismosSegurancaPage = lazy(() => import("@/pages/seo/EN81MecanismosSegurancaPage"));

const CustosModernizacaoElevadoresPage = lazy(() => import("@/pages/seo/CustosModernizacaoElevadoresPage"));
const FalhasElevadoresPage = lazy(() => import("@/pages/seo/FalhasElevadoresPage"));
const AcidentesElevadoresPage = lazy(() => import("@/pages/seo/AcidentesElevadoresPage"));
const RiscoOperacionalElevadoresPage = lazy(() => import("@/pages/seo/RiscoOperacionalElevadoresPage"));
const SegurancaPortasElevadorPage = lazy(() => import("@/pages/seo/SegurancaPortasElevadorPage"));
const SindicoElevadoresPage = lazy(() => import("@/pages/seo/SindicoElevadoresPage"));
const GestaoPredialElevadoresPage = lazy(() => import("@/pages/seo/GestaoPredialElevadoresPage"));
const ContratoManutencaoElevadoresPage = lazy(() => import("@/pages/seo/ContratoManutencaoElevadoresPage"));
const CustoElevadoresPage = lazy(() => import("@/pages/seo/CustoElevadoresPage"));

const CalculadoraConsumoElevadorPage = lazy(() => import("@/pages/seo/CalculadoraConsumoElevadorPage"));
const SoftwareElevadoresPage = lazy(() => import("@/pages/seo/SoftwareElevadoresPage"));


const ManutencaoElevadoresPage = lazy(() => import("@/pages/seo/ManutencaoElevadoresPage"));
const GestaoElevadoresPage = lazy(() => import("@/pages/seo/GestaoElevadoresPage"));
const ScoreRiscoElevadoresPage = lazy(() => import("@/pages/seo/ScoreRiscoElevadoresPage"));


const FAQContratosManutencaoPage = lazy(() => import("@/pages/seo/FAQContratosManutencaoPage"));
const FAQElevadoresPage = lazy(() => import("@/pages/seo/FAQElevadoresPage"));
const SinaleticaElevadoresPage = lazy(() => import("@/pages/seo/SinaleticaElevadoresPage"));
const ElevadorResidencialPage = lazy(() => import("@/pages/seo/ElevadorResidencialPage"));
const GuiaElevadorResidencialPage = lazy(() => import("@/pages/seo/GuiaElevadorResidencialPage"));
const EstudoMobilidadeVerticalPage = lazy(() => import("@/pages/seo/EstudoMobilidadeVerticalPage"));
const VidaUtilElevadorPage = lazy(() => import("@/pages/seo/VidaUtilElevadorPage"));
const QuandoModernizarElevadorPage = lazy(() => import("@/pages/seo/QuandoModernizarElevadorPage"));
const TiposElevadoresPage = lazy(() => import("@/pages/seo/TiposElevadoresPage"));
const ElevadorPresoPage = lazy(() => import("@/pages/seo/ElevadorPresoPage"));


const EstatisticasAcidentesElevadoresPage = lazy(() => import("@/pages/seo/EstatisticasAcidentesElevadoresPage"));
const MapaNormasElevadoresPortugalPage = lazy(() => import("@/pages/seo/MapaNormasElevadoresPortugalPage"));

const ComponentesElevadorMapaTecnicoPage = lazy(() => import("@/pages/seo/ComponentesElevadorMapaTecnicoPage"));
const LinhaTempoNormasElevadoresPage = lazy(() => import("@/pages/seo/LinhaTempoNormasElevadoresPage"));
const TaxonomiaFalhasElevadoresPage = lazy(() => import("@/pages/seo/TaxonomiaFalhasElevadoresPage"));
const RecursosTecnicosElevadoresPage = lazy(() => import("@/pages/seo/RecursosTecnicosElevadoresPage"));
const LimitadorVelocidadePage = lazy(() => import("@/pages/seo/glossario/LimitadorVelocidadePage"));
const ParaquedasElevadorPage = lazy(() => import("@/pages/seo/glossario/ParaquedasElevadorPage"));
const FundoDoPocoPage = lazy(() => import("@/pages/seo/glossario/FundoDoPocoPage"));
const CasaDasMaquinasPage = lazy(() => import("@/pages/seo/glossario/CasaDasMaquinasPage"));
const MaquinaDeTracaoPage = lazy(() => import("@/pages/seo/glossario/MaquinaDeTracaoPage"));
const AmortecedoresElevadorPage = lazy(() => import("@/pages/seo/glossario/AmortecedoresElevadorPage"));
const QuadroDeMAnobraPage = lazy(() => import("@/pages/seo/glossario/QuadroDeMAnobraPage"));
const CentralHidraulicaPage = lazy(() => import("@/pages/seo/glossario/CentralHidraulicaPage"));
const AgendarChamadaPage = lazy(() => import("@/pages/AgendarChamadaPage"));
const AgendarConfirmacaoPage = lazy(() => import("@/pages/AgendarConfirmacaoPage"));
const ChamadaDiagnosticoPage = lazy(() => import("@/pages/ChamadaDiagnosticoPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const ConsultingPaymentSuccessPage = lazy(() => import("@/pages/ConsultingPaymentSuccessPage"));
const PaymentCanceledPage = lazy(() => import("@/pages/PaymentCanceledPage"));
const InstallAppPage = lazy(() => import("@/pages/InstallAppPage"));
const ObrigadoPage = lazy(() => import("@/pages/ObrigadoPage"));



const NormasTransporteVerticalPilarPage = lazy(() => import("@/pages/seo/NormasTransporteVerticalPilarPage"));
const InspecaoElevadoresObrigatoriaPage = lazy(() => import("@/pages/seo/InspecaoElevadoresObrigatoriaPage"));

const NormasElevadoresEN81PortugalPage = lazy(() => import("@/pages/seo/NormasElevadoresEN81PortugalPage"));






const EN8180ExistentesPage = lazy(() => import("@/pages/seo/EN8180ExistentesPage"));
const DL320Page = lazy(() => import("@/pages/seo/DL320Page"));
const Diretiva201433Page = lazy(() => import("@/pages/seo/Diretiva201433Page"));
const PlataformasElevatoriasPage = lazy(() => import("@/pages/seo/PlataformasElevatoriasPage"));


const FerramentasElevadoresHubPage = lazy(() => import("@/pages/tools/FerramentasElevadoresHubPage"));

const CalculadoraModernizacaoElevadorPage = lazy(() => import("@/pages/tools/CalculadoraModernizacaoElevadorPage"));

// Comparison pages
const ElevadorHidraulicoVsTracaoPage = lazy(() => import("@/pages/seo/ElevadorHidraulicoVsTracaoPage"));
const SubstituicaoVsModernizacaoPage = lazy(() => import("@/pages/seo/SubstituicaoVsModernizacaoPage"));
const ManutencaoPreventivaVsCorretivaPage = lazy(() => import("@/pages/seo/ManutencaoPreventivaVsCorretivaPage"));
const ElevadorCasaMaquinasVsMRLPage = lazy(() => import("@/pages/seo/ElevadorCasaMaquinasVsMRLPage"));
const ComparativoMarcasElevadoresPage = lazy(() => import("@/pages/seo/ComparativoMarcasElevadoresPage"));

// Loading fallback component
function PageLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

export function AnimatedRoutes() {
  const location = useLocation();

  return (
    <>
      <ScrollToTop />
      <RouteErrorBoundary>
        <Suspense fallback={<PageLoadingFallback />}>
          <div key={location.pathname}>
          <Routes location={location}>
          {/* Public routes */}
          <Route path="/" element={<PageTransitionWrapper><Index /></PageTransitionWrapper>} />
          <Route path="/auth" element={<PageTransitionWrapper><ClerkAuthPage /></PageTransitionWrapper>} />
          <Route path="/create-organization" element={<PageTransitionWrapper><CreateOrganizationPage /></PageTransitionWrapper>} />
          <Route path="/como-funciona" element={<PageTransitionWrapper><ComoFuncionaPage /></PageTransitionWrapper>} />
          {/* Main consulting plans page */}
          <Route path="/planos-consultoria" element={<PageTransitionWrapper><PlanosConsultoriaPage /></PageTransitionWrapper>} />
          {/* Redirect old plan routes to /planos-consultoria */}
          {/* /planos — canonical page in registry; renders the same plans content */}
          <Route path="/planos" element={<PageTransitionWrapper><PlanosConsultoriaPage /></PageTransitionWrapper>} />
          {/* /consultoria → 301 to /consultoria-elevadores via public/_redirects (Cloudflare) */}
          {/* /ajuda — registered as core entry; renders FAQ content */}
          <Route path="/ajuda" element={<PageTransitionWrapper><FAQPage /></PageTransitionWrapper>} />
          <Route path="/changelog" element={<PageTransitionWrapper><ChangelogPage /></PageTransitionWrapper>} />
          <Route path="/investidores" element={<PageTransitionWrapper><InvestorsPage /></PageTransitionWrapper>} />
          <Route path="/parceiros" element={<PageTransitionWrapper><PartnersPage /></PageTransitionWrapper>} />
          <Route path="/templates" element={<PageTransitionWrapper><EmailTemplatesPage /></PageTransitionWrapper>} />
          <Route path="/call-script" element={<PageTransitionWrapper><CallScriptPage /></PageTransitionWrapper>} />
          <Route path="/investor-deck" element={<PageTransitionWrapper><InvestorDeckPage /></PageTransitionWrapper>} />
          <Route path="/investor-deck-visual" element={<PageTransitionWrapper><InvestorDeckVisualPage /></PageTransitionWrapper>} />
          <Route path="/meeting-playbook" element={<PageTransitionWrapper><MeetingPlaybookPage /></PageTransitionWrapper>} />
          <Route path="/faq-due-diligence" element={<PageTransitionWrapper><FAQDueDiligencePage /></PageTransitionWrapper>} />
          <Route path="/qa-financeiro" element={<PageTransitionWrapper><QAFinanceiroPage /></PageTransitionWrapper>} />
          <Route path="/learning-loop" element={<PageTransitionWrapper><LearningLoopPage /></PageTransitionWrapper>} />
          <Route path="/politica-dados" element={<PageTransitionWrapper><DataPolicyPage /></PageTransitionWrapper>} />
          <Route path="/resposta-auditoria" element={<PageTransitionWrapper><AuditResponsePage /></PageTransitionWrapper>} />
          <Route path="/reset-password" element={<PageTransitionWrapper><ResetPasswordPage /></PageTransitionWrapper>} />
          <Route path="/auditoria-contrato-manutencao-elevadores" element={<PageTransitionWrapper><AuditoriaContratoManutencaoPage /></PageTransitionWrapper>} />
          <Route path="/segunda-opiniao-tecnica-elevadores" element={<PageTransitionWrapper><SegundaOpiniaoTecnicaPage /></PageTransitionWrapper>} />
          <Route path="/quanto-custa-auditoria-tecnica-elevadores" element={<PageTransitionWrapper><CustoAuditoriaTecnicaPage /></PageTransitionWrapper>} />
          <Route path="/termos" element={<PageTransitionWrapper><TermsPage /></PageTransitionWrapper>} />
          <Route path="/inspecoes-periodicas-elevadores" element={<PageTransitionWrapper><InspecoesPeriodicasPublicPage /></PageTransitionWrapper>} />
          <Route path="/politica-privacidade" element={<PageTransitionWrapper><PrivacyPolicyPage /></PageTransitionWrapper>} />
          <Route path="/legal/members-terms" element={<PageTransitionWrapper><MembersTermsPage /></PageTransitionWrapper>} />
          <Route path="/legal/refunds" element={<PageTransitionWrapper><RefundPolicyPage /></PageTransitionWrapper>} />
          {/* /return-policy → 301 to /legal/refunds via public/_redirects (Cloudflare) */}
          <Route path="/nota-de-confianca" element={<PageTransitionWrapper><NotaConfiancaPage /></PageTransitionWrapper>} />
          <Route path="/livro-reclamacoes" element={<PageTransitionWrapper><LivroReclamacoesPage /></PageTransitionWrapper>} />
          <Route path="/contactos" element={<PageTransitionWrapper><ContactosPage /></PageTransitionWrapper>} />
          <Route path="/diagnostico-elevador" element={<PageTransitionWrapper><DiagnosticoElevadorPage /></PageTransitionWrapper>} />
          <Route path="/inspecao-elevadores-obrigatoria" element={<PageTransitionWrapper><InspecaoElevadoresObrigatoriaPage /></PageTransitionWrapper>} />
          <Route path="/normas-elevadores-en-81-portugal" element={<PageTransitionWrapper><NormasElevadoresEN81PortugalPage /></PageTransitionWrapper>} />
          <Route path="/politica-imagens-ia" element={<PageTransitionWrapper><PoliticaImagensIAPage /></PageTransitionWrapper>} />
          <Route path="/governanca-dados" element={<PageTransitionWrapper><GovernancaDadosPage /></PageTransitionWrapper>} />
          <Route path="/sobre" element={<PageTransitionWrapper><SobrePage /></PageTransitionWrapper>} />
          <Route path="/engenheiro-responsavel" element={<PageTransitionWrapper><EngenheiroResponsavelPage /></PageTransitionWrapper>} />
          <Route path="/faq" element={<PageTransitionWrapper><FAQPage /></PageTransitionWrapper>} />
          <Route path="/agendar-chamada" element={<PageTransitionWrapper><AgendarChamadaPage /></PageTransitionWrapper>} />
          <Route path="/agendar-chamada/confirmacao" element={<PageTransitionWrapper><AgendarConfirmacaoPage /></PageTransitionWrapper>} />
          <Route path="/chamada-diagnostico" element={<PageTransitionWrapper><ChamadaDiagnosticoPage /></PageTransitionWrapper>} />
          <Route path="/payment-success" element={<PageTransitionWrapper><PaymentSuccessPage /></PageTransitionWrapper>} />
          <Route path="/payment-success-consulting" element={<PageTransitionWrapper><ConsultingPaymentSuccessPage /></PageTransitionWrapper>} />
          <Route path="/payment-canceled" element={<PageTransitionWrapper><PaymentCanceledPage /></PageTransitionWrapper>} />
          <Route path="/obrigado" element={<PageTransitionWrapper><ObrigadoPage /></PageTransitionWrapper>} />
          <Route path="/install" element={<PageTransitionWrapper><InstallAppPage /></PageTransitionWrapper>} />
          
          {/* Members - Auth page is public (login/signup) */}
          <Route path="/members/auth" element={<PageTransitionWrapper><MembersAuthPage /></PageTransitionWrapper>} />
          <Route path="/members/sucesso" element={<PageTransitionWrapper><MembersSuccessPage /></PageTransitionWrapper>} />
          
          {/* Members landing page - now protected */}
          <Route path="/members" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><MembersPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          
          {/* Legacy route - redirect to /members/vip */}
          {/* Onboarding - requires auth but not onboarding completion */}
          <Route path="/onboarding" element={
            <ProtectedMemberRoute requireVIP={false} requireOnboarding={false}>
              <PageTransitionWrapper><OnboardingPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          
          {/* Free Dashboard - authenticated users who completed onboarding */}
          
          {/* Members - Protected routes (require auth + subscription) */}
          <Route path="/members/dashboard" element={
            <ProtectedMemberRoute requireVIP={true}>
              <PageTransitionWrapper><MembersDashboardPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/onboarding" element={
            <ProtectedMemberRoute>
              <PageTransitionWrapper><MembersOnboardingPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/news" element={
            <ProtectedMemberRoute>
              <PageTransitionWrapper><MembersNewsPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/support" element={
            <ProtectedMemberRoute>
              <PageTransitionWrapper><MembersSupportPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/chat" element={
            <ProtectedMemberRoute>
              <PageTransitionWrapper><MembersChatPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/account" element={
            <ProtectedMemberRoute>
              <PageTransitionWrapper><MembersAccountPage2 /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/admin" element={
            <ProtectedMemberRoute>
              <PageTransitionWrapper><MembersAdminPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          {/* /members/conta redirects to /members/account for consistency */}
          <Route path="/members/conta" element={<Navigate to="/members/account" replace />} />
          
          {/* Upgrade page for free users */}
          <Route path="/members/upgrade" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><UpgradePage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          
          {/* Members - Glossary (accessible to all authenticated users) */}
          <Route path="/members/glossario" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><GlossaryPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/glossario/:slug" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><GlossaryTermPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          
          {/* Members - Checklists */}
          <Route path="/members/checklists" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><ChecklistsPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          
{/* Members - Comparisons */}
          <Route path="/members/comparacoes" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><ComparisonsPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/comparacoes/:slug" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><ComparisonDetailPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          
          {/* Members - Inspections */}
          <Route path="/members/inspecoes" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><InspectionsPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />

          {/* Members - VIP Exclusive */}
          <Route path="/members/vip" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><VIPDashboardPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
<Route path="/members/vip/casos" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><VIPCasesPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/vip/casos/:slug" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><VIPCaseDetailPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/vip/pareceres" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><VIPOpinionsPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/vip/pareceres/:slug" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><VIPOpinionDetailPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/vip/riscos" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><VIPRiskAnalysesPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/vip/riscos/:slug" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><VIPRiskAnalysisDetailPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          <Route path="/members/vip/templates" element={
            <ProtectedMemberRoute requireVIP={false}>
              <PageTransitionWrapper><VIPTemplatesPage /></PageTransitionWrapper>
            </ProtectedMemberRoute>
          } />
          
          {/* Admin - Glossary Management */}
          <Route path="/admin/glossario" element={
            <ProtectedRoute requireOrganization={false}>
              <PageTransitionWrapper><AdminGlossaryPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          
          {/* Admin - SEO Report (uses Members Auth) */}
          <Route path="/admin/seo-report" element={
            <ProtectedAdminMemberRoute>
              <PageTransitionWrapper><AdminSeoReportPage /></PageTransitionWrapper>
            </ProtectedAdminMemberRoute>
          } />

          {/* Admin - SEO War Room (uses Members Auth) */}
          <Route path="/admin/seo-war-room" element={
            <ProtectedAdminMemberRoute>
              <PageTransitionWrapper><AdminSeoWarRoomPage /></PageTransitionWrapper>
            </ProtectedAdminMemberRoute>
          } />
          
          {/* Admin - Inventário Editorial */}
          <Route path="/admin/inventario" element={
            <ProtectedRoute requireOrganization={false}>
              <PageTransitionWrapper><AdminInventarioPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/monitor-normas" element={
            <ProtectedRoute requireOrganization={false}>
              <PageTransitionWrapper><AdminMonitorNormasPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          
          {/* SEO Pages - Main Pillar */}
          <Route path="/elevadores" element={<PageTransitionWrapper><ElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/certificacao-elevadores-portugal" element={<PageTransitionWrapper><CertificacaoElevadoresPortugalPage /></PageTransitionWrapper>} />
          <Route path="/modernizacao-elevadores" element={<PageTransitionWrapper><ModernizacaoElevadoresPage /></PageTransitionWrapper>} />
          
          {/* SEO Pages - Compliance & Inspection Pillar */}
          <Route path="/auditoria-elevadores" element={<PageTransitionWrapper><AuditoriaElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/checklist-elevadores" element={<PageTransitionWrapper><ChecklistElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/sinaletica-para-elevadores" element={<PageTransitionWrapper><SinaleticaElevadoresPage /></PageTransitionWrapper>} />
          
          {/* SEO Pages - EN 81 & Standards Pillar - Conditional (preview for guests, full for members) */}
          <Route path="/en81-20" element={<PageTransitionWrapper><ConditionalEN8120Page /></PageTransitionWrapper>} />
          <Route path="/en81-50" element={<PageTransitionWrapper><ConditionalEN8150Page /></PageTransitionWrapper>} />
          <Route path="/en81-acessibilidade" element={<PageTransitionWrapper><ConditionalEN81AcessibilidadePage /></PageTransitionWrapper>} />
          <Route path="/en81-incendio" element={<PageTransitionWrapper><ConditionalEN81IncendioPage /></PageTransitionWrapper>} />
          
          {/* SEO Pages - Normative Cluster (Topic Cluster around /mapa-normas-transporte-vertical) */}
          <Route path="/en-81-80-elevadores-existentes" element={<PageTransitionWrapper><EN8180ExistentesPage /></PageTransitionWrapper>} />
          <Route path="/dl-320-2002-elevadores" element={<PageTransitionWrapper><DL320Page /></PageTransitionWrapper>} />
          <Route path="/diretiva-2014-33-ue-elevadores" element={<PageTransitionWrapper><Diretiva201433Page /></PageTransitionWrapper>} />
          <Route path="/normas-plataformas-elevatorias" element={<PageTransitionWrapper><PlataformasElevatoriasPage /></PageTransitionWrapper>} />
          
          {/* Pillar page: Normas de Transporte Vertical */}
          <Route path="/normas-transporte-vertical-portugal" element={<PageTransitionWrapper><NormasTransporteVerticalPilarPage /></PageTransitionWrapper>} />
          
          {/* SEO Pages - Safety & Risk Pillar */}
          <Route path="/seguranca-elevadores" element={<PageTransitionWrapper><SegurancaElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/falhas-elevadores" element={<PageTransitionWrapper><FalhasElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/acidentes-elevadores" element={<PageTransitionWrapper><AcidentesElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/risco-operacional-elevadores" element={<PageTransitionWrapper><RiscoOperacionalElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/seguranca-portas-elevador" element={<PageTransitionWrapper><SegurancaPortasElevadorPage /></PageTransitionWrapper>} />
          <Route path="/falhas-comuns-sistemas-seguranca-elevadores" element={<PageTransitionWrapper><FalhasComunsSistemasSegurancaPage /></PageTransitionWrapper>} />
          <Route path="/checklist-inspecao-seguranca-elevadores" element={<PageTransitionWrapper><ChecklistInspecaoSegurancaPage /></PageTransitionWrapper>} />
          <Route path="/en81-mecanismos-seguranca-elevadores" element={<PageTransitionWrapper><EN81MecanismosSegurancaPage /></PageTransitionWrapper>} />
          <Route path="/custos-modernizacao-elevadores-2026" element={<PageTransitionWrapper><CustosModernizacaoElevadoresPage /></PageTransitionWrapper>} />

          {/* SEO Pages - Service Cluster (Auditoria / Consultoria / Diagnóstico) */}
          <Route path="/consultoria-elevadores" element={<PageTransitionWrapper><ConsultoriaElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/consultor-elevadores-condominio" element={<PageTransitionWrapper><ConsultorElevadoresCondominioPage /></PageTransitionWrapper>} />
          <Route path="/auditoria-elevadores-condominio" element={<PageTransitionWrapper><AuditoriaElevadoresCondominioPage /></PageTransitionWrapper>} />
          
          {/* SEO Pages - Building & Management Pillar */}
          <Route path="/sindico-elevadores" element={<PageTransitionWrapper><SindicoElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/gestao-predial-elevadores" element={<PageTransitionWrapper><GestaoPredialElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/contrato-manutencao-elevadores" element={<PageTransitionWrapper><ContratoManutencaoElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/faq-contratos-manutencao-elevadores" element={<PageTransitionWrapper><FAQContratosManutencaoPage /></PageTransitionWrapper>} />
          <Route path="/faq-elevadores" element={<PageTransitionWrapper><FAQElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/custo-elevadores" element={<PageTransitionWrapper><CustoElevadoresPage /></PageTransitionWrapper>} />
          
          {/* SEO Pages - Product & Software Pillar */}
          <Route path="/software-elevadores" element={<PageTransitionWrapper><SoftwareElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/manutencao-elevadores" element={<PageTransitionWrapper><ManutencaoElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/gestao-elevadores" element={<PageTransitionWrapper><GestaoElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/score-risco-elevadores" element={<PageTransitionWrapper><ScoreRiscoElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/calculadora-consumo-elevador" element={<PageTransitionWrapper><CalculadoraConsumoElevadorPage /></PageTransitionWrapper>} />
          <Route path="/ferramentas-elevadores" element={<PageTransitionWrapper><FerramentasElevadoresHubPage /></PageTransitionWrapper>} />
          <Route path="/analise-contrato-elevador" element={<PageTransitionWrapper><AnaliseContratoElevadorPage /></PageTransitionWrapper>} />
          <Route path="/calculadora-modernizacao-elevador" element={<PageTransitionWrapper><CalculadoraModernizacaoElevadorPage /></PageTransitionWrapper>} />
          <Route path="/simulador-risco-elevador" element={<PageTransitionWrapper><SimuladorRiscoElevadorPage /></PageTransitionWrapper>} />
          <Route path="/elevador-residencial" element={<PageTransitionWrapper><ElevadorResidencialPage /></PageTransitionWrapper>} />
          <Route path="/guia-elevador-residencial-portugal-2026" element={<PageTransitionWrapper><GuiaElevadorResidencialPage /></PageTransitionWrapper>} />
          <Route path="/estudo-mobilidade-vertical-portugal-2026" element={<PageTransitionWrapper><EstudoMobilidadeVerticalPage /></PageTransitionWrapper>} />
          
          {/* SEO Pages - Modernização & Ciclo de Vida */}
          <Route path="/vida-util-elevador" element={<PageTransitionWrapper><VidaUtilElevadorPage /></PageTransitionWrapper>} />
          <Route path="/quando-modernizar-elevador" element={<PageTransitionWrapper><QuandoModernizarElevadorPage /></PageTransitionWrapper>} />
          <Route path="/tipos-elevadores" element={<PageTransitionWrapper><TiposElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/elevador-preso-o-que-fazer" element={<PageTransitionWrapper><ElevadorPresoPage /></PageTransitionWrapper>} />
          
          {/* SEO Pages - Comparison Pages */}
          <Route path="/elevador-hidraulico-vs-tracao" element={<PageTransitionWrapper><ElevadorHidraulicoVsTracaoPage /></PageTransitionWrapper>} />
          <Route path="/substituicao-elevador-vs-modernizacao" element={<PageTransitionWrapper><SubstituicaoVsModernizacaoPage /></PageTransitionWrapper>} />
          <Route path="/manutencao-preventiva-vs-corretiva-elevadores" element={<PageTransitionWrapper><ManutencaoPreventivaVsCorretivaPage /></PageTransitionWrapper>} />
          <Route path="/elevador-com-casa-maquinas-vs-mrl" element={<PageTransitionWrapper><ElevadorCasaMaquinasVsMRLPage /></PageTransitionWrapper>} />
          <Route path="/comparativo-marcas-elevadores" element={<PageTransitionWrapper><ComparativoMarcasElevadoresPage /></PageTransitionWrapper>} />

          
          {/* SEO Pages - Linkable Assets */}
          <Route path="/estatisticas-acidentes-elevadores" element={<PageTransitionWrapper><EstatisticasAcidentesElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/mapa-normas-elevadores-portugal" element={<PageTransitionWrapper><MapaNormasElevadoresPortugalPage /></PageTransitionWrapper>} />
          <Route path="/componentes-elevador-mapa-tecnico" element={<PageTransitionWrapper><ComponentesElevadorMapaTecnicoPage /></PageTransitionWrapper>} />
          <Route path="/linha-tempo-normas-elevadores" element={<PageTransitionWrapper><LinhaTempoNormasElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/taxonomia-falhas-elevadores" element={<PageTransitionWrapper><TaxonomiaFalhasElevadoresPage /></PageTransitionWrapper>} />
          <Route path="/recursos-tecnicos-elevadores" element={<PageTransitionWrapper><RecursosTecnicosElevadoresPage /></PageTransitionWrapper>} />
          
          {/* SEO Pages - Glossário Público */}
          <Route path="/glossario/limitador-de-velocidade-elevador" element={<PageTransitionWrapper><LimitadorVelocidadePage /></PageTransitionWrapper>} />
          <Route path="/glossario/paraquedas-elevador" element={<PageTransitionWrapper><ParaquedasElevadorPage /></PageTransitionWrapper>} />
          <Route path="/glossario/fundo-do-poco-elevador" element={<PageTransitionWrapper><FundoDoPocoPage /></PageTransitionWrapper>} />
          <Route path="/glossario/casa-das-maquinas-elevador" element={<PageTransitionWrapper><CasaDasMaquinasPage /></PageTransitionWrapper>} />
          <Route path="/glossario/maquina-de-tracao" element={<PageTransitionWrapper><MaquinaDeTracaoPage /></PageTransitionWrapper>} />
          <Route path="/glossario/amortecedores-elevador" element={<PageTransitionWrapper><AmortecedoresElevadorPage /></PageTransitionWrapper>} />
          <Route path="/glossario/quadro-de-manobra-elevador" element={<PageTransitionWrapper><QuadroDeMAnobraPage /></PageTransitionWrapper>} />
          <Route path="/glossario/central-hidraulica-elevador" element={<PageTransitionWrapper><CentralHidraulicaPage /></PageTransitionWrapper>} />
          
          {/* Protected routes - require auth + organization */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <PageTransitionWrapper><ClientDashboard /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/request-audit" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AuditRequestPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminDashboard /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/audit/:id" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminAuditDetail /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/pedidos" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminAuditRequestsPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/pedidos/:id" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminAuditRequestDetailPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/members" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminMembersPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/management" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminManagementPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/audit-logs" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminAuditLogsPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/news" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminNewsPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/templates" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminTemplatesPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/documents" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminDocumentsPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/leads" element={
            <ProtectedRoute>
              <PageTransitionWrapper><AdminLeadsPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/governance" element={
            <ProtectedRoute>
              <PageTransitionWrapper><GovernanceDashboard /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/board" element={
            <ProtectedRoute>
              <PageTransitionWrapper><BoardDashboard /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          <Route path="/admin/setup" element={
            <ProtectedRoute requireOrganization={false}>
              <PageTransitionWrapper><SetupAdminPage /></PageTransitionWrapper>
            </ProtectedRoute>
          } />
          
          {/* Legacy route redirect */}
          <Route path="/admin/login" element={<ClerkAuthPage />} />

          {/* In-app redirects for legacy / mistyped internal links (prevents 404 on internal CTAs). */}
          {/* Keep alphabetised by source path for easy auditing. */}
          <Route path="/agendar-diagnostico" element={<Navigate to="/chamada-diagnostico" replace />} />
          <Route path="/analise-contrato-manutencao-elevador" element={<Navigate to="/analise-contrato-elevador" replace />} />
          <Route path="/analises-tecnicas-elevadores" element={<Navigate to="/auditoria-elevadores" replace />} />
          <Route path="/analises-tecnicas-elevadores/modernizacao-elevadores" element={<Navigate to="/modernizacao-elevadores" replace />} />
          <Route path="/auditoria-contrato-manutencao" element={<Navigate to="/auditoria-contrato-manutencao-elevadores" replace />} />
          <Route path="/auditoria-tecnica" element={<Navigate to="/auditoria-elevadores" replace />} />
          <Route path="/auditoria-tecnica-elevadores" element={<Navigate to="/auditoria-elevadores" replace />} />
          <Route path="/avaliacao-tecnica-elevador" element={<Navigate to="/auditoria-elevadores" replace />} />
          <Route path="/clausulas-abusivas-contrato-manutencao-elevador" element={<Navigate to="/contrato-manutencao-elevadores" replace />} />
          <Route path="/clausulas-abusivas-contratos-manutencao-elevadores" element={<Navigate to="/contrato-manutencao-elevadores" replace />} />
          <Route path="/comparar-planos" element={<Navigate to="/planos" replace />} />
          <Route path="/consultoria" element={<Navigate to="/consultoria-elevadores" replace />} />
          <Route path="/contacto" element={<Navigate to="/contactos" replace />} />
          <Route path="/custo-elevadores-estudo-mercado" element={<Navigate to="/custo-elevadores" replace />} />
          <Route path="/custo-modernizacao-elevador" element={<Navigate to="/custos-modernizacao-elevadores-2026" replace />} />
          <Route path="/diagnostico-elevador-predio" element={<Navigate to="/diagnostico-elevador" replace />} />
          <Route path="/en-81-20-2020-elevadores" element={<Navigate to="/en81-20" replace />} />
          <Route path="/en-81-20-ascensores" element={<Navigate to="/en81-20" replace />} />
          <Route path="/en-81-50-componentes" element={<Navigate to="/en81-50" replace />} />
          <Route path="/en81-80" element={<Navigate to="/en-81-80-elevadores-existentes" replace />} />
          <Route path="/erros-elevadores" element={<Navigate to="/falhas-elevadores" replace />} />
          <Route path="/free/dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/glossario/quadro-de-manobra" element={<Navigate to="/glossario/quadro-de-manobra-elevador" replace />} />
          <Route path="/investidores/politica-dados" element={<Navigate to="/politica-dados" replace />} />
          <Route path="/login" element={<Navigate to="/auth" replace />} />
          <Route path="/manutencao-elevadores-portugal" element={<Navigate to="/manutencao-elevadores" replace />} />
          <Route path="/manutencao-preventiva-vs-corretiva" element={<Navigate to="/manutencao-preventiva-vs-corretiva-elevadores" replace />} />
          <Route path="/mapa-normas-transporte-vertical" element={<Navigate to="/mapa-normas-elevadores-portugal" replace />} />
          <Route path="/members-vip" element={<Navigate to="/members/vip" replace />} />
          <Route path="/members/templates" element={<Navigate to="/members/vip/templates" replace />} />
          <Route path="/membros" element={<Navigate to="/members" replace />} />
          <Route path="/modernizacao-elevadores-guia" element={<Navigate to="/modernizacao-elevadores" replace />} />
          <Route path="/norma-en81" element={<Navigate to="/normas-elevadores-en-81-portugal" replace />} />
          <Route path="/normative-graphics-download" element={<Navigate to="/recursos-tecnicos-elevadores" replace />} />
          <Route path="/pedido-auditoria" element={<Navigate to="/auditoria-elevadores" replace />} />
          <Route path="/plans" element={<Navigate to="/planos" replace />} />
          <Route path="/plataforma-auditoria-elevadores" element={<Navigate to="/auditoria-elevadores" replace />} />
          <Route path="/politica-reembolso" element={<Navigate to="/legal/refunds" replace />} />
          <Route path="/problemas-elevadores" element={<Navigate to="/falhas-elevadores" replace />} />
          <Route path="/qa" element={<Navigate to="/faq" replace />} />
          <Route path="/return-policy" element={<Navigate to="/legal/refunds" replace />} />
          <Route path="/seguranca-elevadores-sistemas" element={<Navigate to="/seguranca-elevadores" replace />} />
          {/* /simulador-risco-elevador now renders SimuladorRiscoElevadorPage directly (see above) */}
          <Route path="/substituicao-vs-modernizacao-elevador" element={<Navigate to="/substituicao-elevador-vs-modernizacao" replace />} />
          <Route path="/vertical-transportation-standards-map" element={<Navigate to="/mapa-normas-elevadores-portugal" replace />} />
          <Route path="/vida-util-elevador-guia" element={<Navigate to="/vida-util-elevador" replace />} />

          {/* Catch-all */}
          <Route path="*" element={<PageTransitionWrapper><NotFound /></PageTransitionWrapper>} />
        </Routes>
          </div>
        </Suspense>
      </RouteErrorBoundary>
    </>
  );
}
